import { useEffect, useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import knowledgeApi from '@/lib/knowledge-api'
import type { Document } from './useDocuments'

interface UseDocumentPollingOptions {
  kbId: string
  documents: Document[]
  enabled: boolean
  limit: number
  offset: number
  interval?: number
}

export function useDocumentPolling({ 
  kbId, 
  documents, 
  enabled,
  limit,
  offset,
  interval = 3000 // Poll every 3 seconds (optimized from 1 second)
}: UseDocumentPollingOptions) {
  const queryClient = useQueryClient()
  const pollTimerRef = useRef<number | null>(null)
  const isPollingRef = useRef<boolean>(false)

  useEffect(() => {
    if (!enabled) {
      if (pollTimerRef.current) {
        clearTimeout(pollTimerRef.current)
        pollTimerRef.current = null
      }
      isPollingRef.current = false
      return
    }

    // Find documents in RUNNING state
    const runningDocs = documents.filter(doc => doc.status === 'RUNNING')
    
    if (runningDocs.length === 0) {
      // No running documents, stop polling
      if (pollTimerRef.current) {
        clearTimeout(pollTimerRef.current)
        pollTimerRef.current = null
      }
      isPollingRef.current = false
      return
    }

    // Clear any existing timeout first
    if (pollTimerRef.current) {
      clearTimeout(pollTimerRef.current)
      pollTimerRef.current = null
    }

    // Recursive polling function - single batch request for all running documents
    const pollStatus = async () => {
      // Skip if already polling
      if (isPollingRef.current) {
        return
      }

      isPollingRef.current = true
      
      try {
        // Get IDs of running documents for batch request
        const runningDocIds = runningDocs.map(doc => doc.id)
        
        // Single batch request for all running documents
        const response = await knowledgeApi.post(
          `/knowledge/${kbId}/documents/status/batch/`,
          { document_ids: runningDocIds }
        )

        const updatedDocs = response.data.documents || []
        let hasChanges = false
        let allDone = true
        
        // Check for status changes
        for (const updatedDoc of updatedDocs) {
          const originalDoc = runningDocs.find(d => d.id === updatedDoc.id)
          if (originalDoc && updatedDoc.status !== originalDoc.status) {
            hasChanges = true
          }
          if (updatedDoc.status === 'RUNNING') {
            allDone = false
          }
        }
        
        // Force immediate refetch of all document queries for this KB
        if (hasChanges) {
          await queryClient.refetchQueries({ 
            queryKey: ['documents', kbId],
            type: 'active'
          })
        }

        isPollingRef.current = false

        // Schedule next poll only if there are still RUNNING documents
        if (!allDone) {
          pollTimerRef.current = window.setTimeout(pollStatus, interval)
        }
      } catch (error) {
        console.error('[Polling] Error polling document status:', error)
        isPollingRef.current = false
        // On error, retry after interval
        pollTimerRef.current = window.setTimeout(pollStatus, interval)
      }
    }

    // Start initial poll
    pollStatus()

    // Cleanup
    return () => {
      if (pollTimerRef.current) {
        clearTimeout(pollTimerRef.current)
        pollTimerRef.current = null
      }
      isPollingRef.current = false
    }
  }, [kbId, documents, enabled, limit, offset, interval, queryClient])
}
